'use client';

import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';

import { cn } from '@/app/_utils/cn';

interface IFunnelEmailPopoverCtaProps {
  label: string;
  email: string;
  className: string;
  onTrackedClick?: () => void;
}

async function copyToClipboardFallback(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const COPIED_HINT_MS = 2800;

/** 팝오버 열림과 동시에 주소 복사(두 번째 클릭 최소화) — 실패 시에만 버튼으로 재시도 */
const FunnelEmailPopoverCta = ({ label, email, className, onTrackedClick }: IFunnelEmailPopoverCtaProps) => {
  const [copied, setCopied] = React.useState(false);
  const copiedTimerRef = React.useRef<number | null>(null);

  const runCopy = React.useCallback(async () => {
    const ok = await copyToClipboardFallback(email);
    if (ok) {
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        copiedTimerRef.current = null;
      }, COPIED_HINT_MS);
    }
    return ok;
  }, [email]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) {
        void runCopy();
      } else {
        if (copiedTimerRef.current) {
          window.clearTimeout(copiedTimerRef.current);
          copiedTimerRef.current = null;
        }
        setCopied(false);
      }
    },
    [runCopy],
  );

  React.useEffect(
    () => () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  return (
    <PopoverPrimitive.Root onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button type='button' className={className} onClick={() => onTrackedClick?.()}>
          {label}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={10}
          align='center'
          collisionPadding={16}
          style={{ zIndex: 10_050 }}
          className={cn(
            'pointer-events-auto',
            'max-w-[min(100vw-2rem,20rem)] rounded-xl border border-border/85 bg-white px-4 py-3 text-foreground shadow-xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <p className='mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground'>Contact</p>
          <div
            className={cn(
              'flex min-w-0 items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2',
              'font-mono text-sm text-foreground',
            )}
          >
            <span className='min-w-0 flex-1 break-all selection:bg-accent/20'>{email}</span>
            <button
              type='button'
              className={cn(
                'shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors',
                'hover:bg-white hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/45 focus-visible:ring-offset-2',
              )}
              aria-label={copied ? 'Copied' : 'Copy email again'}
              onClick={(e) => {
                e.stopPropagation();
                void runCopy();
              }}
            >
              {copied ? <Check aria-hidden className='size-4 text-[hsl(var(--accent))]' /> : <Copy aria-hidden className='size-4' />}
            </button>
          </div>
          <p className='mt-2.5 text-xs leading-snug text-muted-foreground'>
            {copied
              ? 'Copied to clipboard — paste into Gmail, Outlook, or any mail app.'
              : 'Opening copies the address automatically. Tap the icon again if needed.'}
          </p>
          <a
            href={`mailto:${email}`}
            className={cn(
              'mt-3 inline-flex items-center rounded-md border border-border/70 bg-white px-2.5 py-1.5 text-xs font-medium text-foreground',
              'transition-colors hover:bg-muted/45',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/45 focus-visible:ring-offset-2',
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Open mail app
          </a>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

export default FunnelEmailPopoverCta;
